import { toCompanyTag, withoutBrief } from "@/lib/company-tag";
import { hasReliableCompanyIdentifier } from "@/lib/company-mention-reliability";
import {
  companyResolutionKey,
  type CompanyResolutionParams,
} from "@/lib/company-resolution";
import type { CompanyTag } from "@shaarp/common";
import {
  Children,
  isValidElement,
  type ElementType,
  type ReactNode,
} from "react";
import type { ExtraProps } from "streamdown";

export type CompanyMentionNodeProps = ExtraProps & {
  id?: string;
  name?: string;
  domain?: string;
  siren?: string;
  siret?: string;
  linkedin?: string;
  city?: string;
  address?: string;
  activity?: string;
  /** One or more http(s) URLs, space separated. */
  source?: string;
  brief?: string;
};

/** Reads a string attribute off the parsed node as a fallback to direct props. */
function readNodeProperty(
  node: ExtraProps["node"],
  key: string,
): string | undefined {
  const value = node?.properties?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// rehype-sanitize (used by Streamdown) prefixes `id` and `name` attribute
// values with this constant to prevent DOM clobbering. We strip it to recover
// the original company id / name carried by the marker.
const CLOBBER_PREFIX = "user-content-";

function stripClobberPrefix(value: string | undefined): string | undefined {
  return value?.startsWith(CLOBBER_PREFIX)
    ? value.slice(CLOBBER_PREFIX.length)
    : value;
}

/**
 * Reads the identity a `<company>` marker carries, taking each attribute off the
 * parsed node when Streamdown didn't pass it as a prop and undoing the
 * sanitizer's clobber-prefixing.
 */
export function companyMentionParams({
  id,
  name,
  domain,
  siren,
  siret,
  linkedin,
  city,
  node,
}: CompanyMentionNodeProps): CompanyResolutionParams {
  return {
    id: stripClobberPrefix(id ?? readNodeProperty(node, "id")),
    name: stripClobberPrefix(name ?? readNodeProperty(node, "name")),
    // Unlike id/name, these aren't DOM-clobbering-prone, so rehype-sanitize
    // leaves them unprefixed — read them as-is.
    domain: domain ?? readNodeProperty(node, "domain"),
    siren: siren ?? readNodeProperty(node, "siren"),
    siret: siret ?? readNodeProperty(node, "siret"),
    linkedin: linkedin ?? readNodeProperty(node, "linkedin"),
    city: city ?? readNodeProperty(node, "city"),
  };
}

/**
 * The tag a `<company>` marker describes — everything the assistant said about
 * the company, not only who it is — or null when it carries no name. The chip
 * hands it to the preview as is, so the click needs no lookup.
 */
export function companyTagOfMention(
  props: CompanyMentionNodeProps,
): CompanyTag | null {
  const { node, address, activity, source, brief } = props;
  return toCompanyTag({
    ...companyMentionParams(props),
    address: address ?? readNodeProperty(node, "address"),
    activity: activity ?? readNodeProperty(node, "activity"),
    source: source ?? readNodeProperty(node, "source"),
    brief: brief ?? readNodeProperty(node, "brief"),
  });
}

export type CompanyMentionCollection = {
  /** The mentions kept — each carries a reliable identifier and can be resolved. */
  readonly mentions: CompanyResolutionParams[];
  /**
   * The tags, in reading order, of the mentions without a reliable identifier.
   * Every one is still a company the table names: saving the table saves it
   * from its tag rather than leaving it out. Without the `brief`, which is the
   * conversation's and never leaves it for a save.
   */
  readonly unidentified: CompanyTag[];
};

/** A mention without a reliable identifier, with the key it would resolve by. */
type UnidentifiedMention = { readonly key: string | null; readonly tag: CompanyTag };

/**
 * Collects, in reading order and deduplicated by resolution key, the identity of
 * every company chip in an already-rendered markdown subtree, split into the
 * mentions an identifier resolves and the tags of the others, saved from them.
 *
 * Streamdown hands a component its rendered children rather than the source
 * markdown, so walking those elements is how a container (e.g. a markdown table)
 * learns which companies it cites. `mentionType` is the component the
 * `<company>` tag is rendered with — passed in so this module stays free of
 * component imports.
 */
export function collectCompanyMentions(
  children: ReactNode,
  mentionType: ElementType,
): CompanyMentionCollection {
  const mentions: CompanyResolutionParams[] = [];
  const reliableKeys = new Set<string>();
  // A mention with no reliable identifier is saved from its tag, not resolved.
  // It never claims a key: the identified mention of the same company may come
  // after it, and covers it (`unidentifiedOf`).
  const unreliable: UnidentifiedMention[] = [];

  const visit = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;

      if (child.type === mentionType) {
        const props = child.props as CompanyMentionNodeProps;
        const params = companyMentionParams(props);
        const key = companyResolutionKey(params);

        if (!hasReliableCompanyIdentifier(params)) {
          const tag = companyTagOfMention(props);
          if (tag) unreliable.push({ key, tag: withoutBrief(tag) });
          return;
        }

        if (key && !reliableKeys.has(key)) {
          reliableKeys.add(key);
          mentions.push(params);
        }
        return;
      }

      visit((child.props as { children?: ReactNode }).children);
    });
  };

  visit(children);
  return {
    mentions,
    unidentified: unidentifiedOf(unreliable, coveredKeys(mentions)),
  };
}

/**
 * The keys the identified mentions cover: their own, and — for one with a
 * SIREN — the key of its bare name and town, which "ACME, Lyon" resolves by
 * when "ACME, Lyon, siren" is the same company. Two SIRENs keep two keys.
 */
function coveredKeys(mentions: readonly CompanyResolutionParams[]): Set<string> {
  return new Set(
    mentions
      .flatMap((params) => [
        companyResolutionKey(params),
        companyResolutionKey({ ...params, siren: undefined }),
      ])
      .filter((key): key is string => key !== null),
  );
}

/**
 * Tags of the unidentified mentions no identified chip of the same company
 * covers, once each, in reading order.
 */
function unidentifiedOf(
  unreliable: readonly UnidentifiedMention[],
  coveredKeys: ReadonlySet<string>,
): CompanyTag[] {
  const seenKeys = new Set<string>();
  const tags: CompanyTag[] = [];
  for (const { key, tag } of unreliable) {
    if (key && (coveredKeys.has(key) || seenKeys.has(key))) continue;
    if (key) seenKeys.add(key);
    tags.push(tag);
  }
  return tags;
}
